// src/app/invoices.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  KeyboardAvoidingView,
  TouchableWithoutFeedback,
  Keyboard,
  ScrollView,
  Platform,
} from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IoCalendarOutline } from 'react-icons/io5';
import { MdAdd, MdRemove } from 'react-icons/md';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

// token helper
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

// Mobile detection utility
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      // Check for mobile device using multiple methods
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      // Consider it mobile if it matches mobile user agent OR has small screen with touch support
      setIsMobile(isMobileUA || (isSmallScreen && hasTouchSupport));
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

// --- small helpers for create invoice form ---
const fmtDate = (d) => d.toISOString().split('T')[0];         // YYYY-MM-DD
const fmtTime = (d) => d.toISOString().split('T')[1].slice(0,8); // HH:mm:ss

export default function Invoices() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();

  // Invoice list state
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);

  // Create invoice form state
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customerName, setCustomerName] = useState('');

  const [title, setTitle] = useState('');
  const [jobId, setJobId] = useState(0);
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [workCompletedDate, setWorkCompletedDate] = useState(() => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    return d;
  });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [isNotesVisible, setIsNotesVisible] = useState(false);

  // Preview and email data for after form is cleared - now includes business info
  const [previewInvoiceData, setPreviewInvoiceData] = useState({});
  const [previewCustomerData, setPreviewCustomerData] = useState(null); // Add customer data state
  const [businessInfo, setBusinessInfo] = useState(null);
  const [businessLogoUrl, setBusinessLogoUrl] = useState(null); // Add business logo URL state
  const [lastCreatedInvoiceId, setLastCreatedInvoiceId] = useState(null);

  // derived input bindings for date/time fields - using local time like schedule.jsx
  const dateTimeStr = useMemo(() => {
    if (!(workCompletedDate instanceof Date) || isNaN(workCompletedDate)) return '';
    const pad = (n) => String(n).toString().padStart(2, '0');
    const yyyy = workCompletedDate.getFullYear();
    const mm = pad(workCompletedDate.getMonth() + 1);
    const dd = pad(workCompletedDate.getDate());
    const hh = pad(workCompletedDate.getHours());
    const mi = pad(workCompletedDate.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  }, [workCompletedDate]);

  // inline customer addition
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    notes: '', // Add notes field to initial state
  });

  // Custom Modal Component for better-looking dialogs
  const [customModal, setCustomModal] = useState({
    open: false,
    title: '',
    message: '',
    buttons: []
  });

  // Email state for sending invoices
  const [sendingEmail, setSendingEmail] = useState(false);

  // Payment status state
  const [paymentStatus, setPaymentStatus] = useState('no');

  const showCustomModal = (title, message, buttons) => {
    setCustomModal({
      open: true,
      title,
      message,
      buttons
    });
  };

  const closeCustomModal = () => {
    setCustomModal({
      open: false,
      title: '',
      message: '',
      buttons: []
    });
  };

  const PAGE_SIZE = 25;

  useEffect(() => {
    const titleP = searchParams.get('title');
    const jobIdP = searchParams.get('jobId');
    const descP = searchParams.get('description');
    const priceP = searchParams.get('price');
    const selectedDate = searchParams.get('selectedDate');
    const selectedTime = searchParams.get('selectedTime');
    const customerNameP = searchParams.get('customerName');

    if (titleP) setTitle(titleP);
    if (jobIdP) setJobId(Number(jobIdP) || 0);
    if (descP) setDescription(descP);
    if (priceP) setPrice(String(priceP));
    if (customerNameP) {
      setCustomerName(customerNameP);
      setSearchQuery(customerNameP);
      setSelectedCustomer(customerNameP);
    }

    if (selectedDate && selectedTime) {
      const d = new Date(`${selectedDate}T${selectedTime}Z`);
      if (!isNaN(d.getTime())) setWorkCompletedDate(d);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchCustomersFromDB = async () => {
      try {
        const token = getToken();
        const { data } = await axios.get(`${API_BASE_URL}/api/customers`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const list = Array.isArray(data) ? data : data?.customers ?? [];
        setCustomers(list);

        const cn = searchParams.get('customerName');
        if (cn && list.some((c) => c.name === cn)) {
          setFilteredCustomers([]);
        }
      } catch (err) {
        console.error('Error fetching customers:', err);
      }
    };
    fetchCustomersFromDB();
  }, [searchParams]);

  // Load user permissions on component mount and check access
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          const user_type = parsedPermissions?.user_type;
          const invoicePerm = parsedPermissions?.invoice_perm;

          // Block access if user is NOT (Owner OR Admin) AND has no invoice permission
          if ((user_type !== 'owner' && user_type !== 'admin') && invoicePerm === 'none') {
            alert("You don't have permission to view invoices. Please contact your account administrator.");
            navigate('/'); // Redirect to home page
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  const handleSearch = (q) => {
    setSearchQuery(q);
    if (!q.trim()) {
      setFilteredCustomers([]);
      return;
    }
    const t = q.toLowerCase();
    const matches = customers.filter((c) => (c?.name ?? '').toLowerCase().includes(t));
    setFilteredCustomers(matches);
  };

  const saveInvoice = async () => {
    // Ensure we have a customer name to work with
    const customerToFind = selectedCustomer || searchQuery || customerName || '';
    
    if (!customerToFind.trim()) {
      console.error('Error: No customer selected or entered.');
      showCustomModal(
        'Error',
        'Please select a valid customer from the list or add a new one.',
        [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
      );
      return;
    }

    const selectedCustomerData = customers.find(
      (c) => (c?.name ?? '').trim().toLowerCase() === customerToFind.trim().toLowerCase()
    );

    if (!selectedCustomerData) {
      console.error('Error: Selected customer not found.');
      showCustomModal(
        'Error',
        'Please select a valid customer from the list or add a new one.',
        [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
      );
      return;
    }

    if (!title.trim()) {
      showCustomModal(
        'Error',
        'Please enter a title for the invoice.',
        [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
      );
      return;
    }

    // Handle price: if empty or invalid, default to "0.00"
    let cleanedPrice = price.trim();
    if (cleanedPrice === '' || isNaN(parseFloat(cleanedPrice))) {
      cleanedPrice = '0.00';
    }

    const newInvoice = {
      customer: selectedCustomerData.name,
      customerId: selectedCustomerData.id,
      title: title.trim(),
      description: description.trim() || '', // Save as empty string if blank
      price: cleanedPrice,
      selectedDate: fmtDate(workCompletedDate),
      selectedTime: fmtTime(workCompletedDate),
      jobId: jobId || 0,
      paid: paymentStatus,
      notes: notes.trim() || '', // Save as empty string if blank
    };

    try {
      const token = getToken();
      const response = await axios.post(`${API_BASE_URL}/api/invoices`, newInvoice, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      const invoiceId = response.data?.id;
      const receivedBusinessInfo = response.data?.businessInfo;

      // Store business info from backend response
      if (receivedBusinessInfo) {
        setBusinessInfo(receivedBusinessInfo);
        // Set logo preview if exists
        if (receivedBusinessInfo.businessLogo) {
          setBusinessLogoUrl(`${API_BASE_URL}/api/uploads/${receivedBusinessInfo.businessLogo}`);
        }
      }

      if (!invoiceId) {
        console.error('❌ No invoice ID returned from backend');
        showCustomModal(
          'Warning',
          'Invoice was created but ID was not returned. Please try emailing from the invoices list.',
          [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
        );
        clearForm();
        refreshInvoicesList();
        return;
      }

      // Store invoice and customer data before clearing form
      const invoiceDataForPreview = {
        customer: selectedCustomerData.name,
        title,
        description,
        price: cleanedPrice,
        selectedDate: fmtDate(workCompletedDate),
        selectedTime: fmtTime(workCompletedDate),
        notes,
        paid: paymentStatus, // Add paid status to preview data
      };
      
      setPreviewInvoiceData(invoiceDataForPreview);
      setPreviewCustomerData(selectedCustomerData); // Store customer data for preview
      setLastCreatedInvoiceId(invoiceId);

      // Clear the form and refresh invoices list first
      clearForm();
      refreshInvoicesList();

      // Email function that updates the modal to show "Sending..." state
      const emailInvoicePDF = async () => {
        if (sendingEmail) return;
        
        try {
          setSendingEmail(true);
          
          // Update the modal to show "Sending..." state
          showCustomModal(
            'Invoice Created Successfully! 🎉',
            'Your invoice has been created. What would you like to do next?',
            [
              {
                text: 'Sending…',
                style: 'primary',
                disabled: true,
                onPress: () => {} // Do nothing while sending
              },
              {
                text: '👁️ Preview Invoice',
                style: 'secondary',
                onPress: () => {
                  closeCustomModal();
                  setPreviewOpen(true);
                }
              },
              {
                text: '💾 Save/Print PDF',
                style: 'default',
                onPress: async () => {
                  await generateAndSharePDF(invoiceDataForPreview, invoiceId, receivedBusinessInfo, selectedCustomerData);
                },
              },
              {
                text: '📋 View Invoices',
                style: 'default',
                onPress: closeCustomModal
              }
            ]
          );
          
          // Get user's timezone offset in minutes
          const timezoneOffset = new Date().getTimezoneOffset();
          
          const emailResponse = await axios.post(`${API_BASE_URL}/api/invoices/${invoiceId}/email`, {
            timezoneOffset: timezoneOffset  // Send timezone offset to backend
          }, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
          
          if (emailResponse.data && emailResponse.data.success) {
            showCustomModal(
              'Email Sent Successfully! ✅',
              `Invoice has been emailed as PDF to ${selectedCustomerData.email || selectedCustomerData.name}`,
              [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
            );
          } else {
            showCustomModal(
              'Email Status Unclear ⚠️',
              emailResponse.data?.message || 'Email may have been sent, but status is unclear.',
              [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
            );
          }
        } catch (emailError) {
          console.error('Error sending invoice email:', emailError);
          console.error('Email error response:', emailError.response?.data);
          
          let errorMessage = 'Failed to send email. Please try again.';
          
          if (emailError.response?.status === 400) {
            errorMessage = `Customer email issue: ${selectedCustomerData.email ? 'Email may be invalid' : 'No email address on file'}. Please update customer information.`;
          } else if (emailError.response?.status === 404) {
            errorMessage = 'Invoice not found. Please try creating the invoice again.';
          } else if (emailError.response?.data?.detail) {
            errorMessage = emailError.response.data.detail;
          } else if (emailError.response?.data?.message) {
            errorMessage = emailError.response.data.message;
          } else if (emailError.message) {
            errorMessage = `Network error: ${emailError.message}`;
          }
          
          showCustomModal(
            'Email Failed ❌',
            errorMessage,
            [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
          );
        } finally {
          setSendingEmail(false);
        }
      };

      // Show beautiful three-option modal matching mobile version
      showCustomModal(
        'Invoice Created Successfully! 🎉',
        'Your invoice has been created. What would you like to do next?',
        [
          {
            text: '📧 Email PDF to Customer',
            style: 'primary',
            onPress: emailInvoicePDF
          },
          {
            text: '👁️ Preview Invoice',
            style: 'secondary',
            onPress: () => {
              closeCustomModal();
              setPreviewOpen(true);
            }
          },
          {
            text: '💾 Save/Print PDF',
            style: 'default',
            onPress: async () => {
              await generateAndSharePDF(invoiceDataForPreview, invoiceId, receivedBusinessInfo, selectedCustomerData);
            },
          },
          {
            text: '📋 View Invoices',
            style: 'default',
            onPress: closeCustomModal
          }
        ]
      );
      
    } catch (error) {
      console.error('Error saving invoice:', error);
      console.error('Invoice creation error response:', error.response?.data);
      
      let errorMessage = 'Failed to save invoice.';
      if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.response?.data?.message) {
        errorMessage = `Error: ${error.response.data.message}`;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid invoice data. Please check all fields.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You do not have access to create invoices.';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      showCustomModal(
        'Error',
        errorMessage,
        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
      );
    }
  };

  // Helper function to clear the form
  const clearForm = () => {
    setTitle('');
    setDescription('');
    setPrice('');
    setNotes('');
    setSearchQuery('');
    setSelectedCustomer('');
    setCustomerName('');
    setFilteredCustomers([]);
    setIsNotesVisible(false);
    setAddingCustomer(false);
    setWorkCompletedDate(() => {
      const d = new Date();
      d.setHours(12, 0, 0, 0);
      return d;
    });
    setNewCustomer({
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: '',
      notes: '', // Reset notes field
    });
  };

  // Helper function to refresh invoices list
  const refreshInvoicesList = () => {
    setInvoices([]);
    setHasMore(true);
    setPage(1);
    loadInvoices(1);
  };

  // Function to generate PDF and trigger browser download - matching invoice-details.jsx
  const generateAndSharePDF = async (invoiceData = null, invoiceId = null, businessData = null, customerData = null) => {
    try {
      // Use passed data or fall back to state
      const dataToUse = invoiceData || previewInvoiceData;
      const idToUse = invoiceId || lastCreatedInvoiceId;
      const businessToUse = businessData || businessInfo;
      const logoToUse = businessData?.businessLogo ? `${API_BASE_URL}/api/uploads/${businessData.businessLogo}` : businessLogoUrl;
      
      // Use passed customer data or fall back to state
      const customer = customerData || previewCustomerData || customers.find(c => c.name === dataToUse.customer);
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
            .header { display: flex; justify-content: space-between; margin-bottom: 30px; border-bottom: 2px solid #dc2626; padding-bottom: 20px; }
            .logo-container { margin-bottom: 10px; min-height: 85px; }
            .business-logo { max-width: 150px; max-height: 80px; object-fit: contain; }
            .company-name { font-size: 24px; font-weight: bold; color: #dc2626; margin-bottom: 10px; }
            .company-details { font-size: 13px; color: #666; line-height: 1.6; }
            .invoice-title { font-size: 28px; font-weight: bold; color: #dc2626; }
            .invoice-details { font-size: 13px; color: #666; margin-top: 8px; }
            .section-title { font-size: 18px; font-weight: bold; margin: 30px 0 15px 0; padding-bottom: 5px; border-bottom: 2px solid #e5e7eb; }
            .customer-info { background: #fef2f2; padding: 20px; border-radius: 8px; border-left: 4px solid #dc2626; }
            .service-item { border: 1px solid #e5e7eb; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .pricing-section { background: #fef2f2; padding: 20px; border: 2px solid #dc2626; border-radius: 8px; margin: 30px 0; }
            .price-row { display: flex; justify-content: space-between; padding: 15px 0; border-bottom: 1px solid #fecaca; }
            .total-row { border-top: 2px solid #dc2626; border-bottom: none; font-size: 20px; font-weight: bold; margin-top: 10px; }
            .payment-section { background: #f0fdf4; padding: 20px; border-radius: 8px; border-left: 4px solid #22c55e; margin: 30px 0; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #e5e7eb; text-align: center; font-size: 13px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <div class="logo-container">
                ${logoToUse ? `<img src="${logoToUse}" alt="Business Logo" class="business-logo" />` : ''}
              </div>
              <div class="company-name">${businessToUse?.businessName || 'Your Business Name'}</div>
              <div class="company-details">
                ${businessToUse?.firstName || businessToUse?.lastName ? `${[businessToUse.firstName, businessToUse.lastName].filter(Boolean).join(' ')}<br>` : ''}
                ${businessToUse?.address || ''}<br>
                ${businessToUse?.city && businessToUse?.state ? `${businessToUse.city}, ${businessToUse.state} ${businessToUse.zip || ''}` : 'City, State'}<br>
                Phone: ${businessToUse?.phone || ''}<br>
                Email: ${businessToUse?.email || ''}
              </div>
            </div>
            <div style="text-align: right;">
              <div class="invoice-title">INVOICE</div>
              <div class="invoice-details">
                Invoice #: INV-${idToUse || 'XXXX'}<br>
                Date: ${new Date().toLocaleDateString()}<br>
                Due Date: ${new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}
              </div>
            </div>
          </div>
          
          <div class="section-title">Invoice To:</div>
          <div class="customer-info">
            <strong>${dataToUse.customer || 'Customer Name'}</strong><br>
            ${customer?.address || ''}<br>
            ${customer?.city && customer?.state ? `${customer.city}, ${customer.state} ${customer.zip || ''}` : ''}<br>
            ${customer?.phone ? `Phone: ${customer.phone}` : ''}<br>
            ${customer?.email ? `Email: ${customer.email}` : ''}
          </div>
          
          <div class="section-title">Services Provided</div>
          <div class="service-item">
            <strong style="font-size: 18px;">${dataToUse.title || 'Service Title'}</strong><br><br>
            ${dataToUse.description || 'No description provided'}<br><br>
            <em style="color: #666;">Work completed on: ${(() => {
              if (dataToUse.selectedDate && dataToUse.selectedTime) {
                const utcDate = new Date(dataToUse.selectedDate + 'T' + dataToUse.selectedTime);
                const localDate = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);
                const formattedDate = localDate.toLocaleDateString();
                const formattedTime = localDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                return `${formattedDate} at ${formattedTime}`;
              }
              return 'Date not specified';
            })()}</em>
          </div>
          
          <div class="pricing-section">
  <div class="price-row">
    <span>Service Total:</span>
    <span><strong>$${parseFloat(dataToUse.price || 0).toFixed(2)}</strong></span>
  </div>
  ${dataToUse.paid === 'yes' ? `
  <div class="price-row" style="border-bottom: none; color: #22c55e; font-weight: bold;">
    <span>Paid in Full</span>
    <span>✓</span>
  </div>
  ` : ''}
  <div class="price-row total-row">
    <span>Amount Due:</span>
    <span>$${dataToUse.paid === 'yes' ? '0.00' : parseFloat(dataToUse.price || 0).toFixed(2)}</span>
  </div>
</div>
          
          <div class="payment-section">
            <strong>Payment Information</strong><br><br>
            • Payment is due within 30 days of invoice date<br>
            • Please include invoice number with payment<br>
            • Late payments may be subject to additional fees<br>
            • Contact us with any questions about this invoice
          </div>
          
          <div class="footer">
            <p>Thank you for your business!</p>
            <p><strong>Questions?</strong> Contact us at ${businessToUse?.phone || ''} or ${businessToUse?.email || ''}</p>
          </div>
        </body>
        </html>
      `;
      
      // Generate PDF filename
      const customerFileName = (dataToUse.customer || 'Customer')
        .replace(/[^a-z0-9\s]/gi, '')  // Remove special characters
        .trim()
        .replace(/\s+/g, '_');  // Replace spaces with underscores
      
      const pdfFileName = `${customerFileName}_Invoice.pdf`;
      
      // Create a temporary iframe to render the HTML and trigger print
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';
      document.body.appendChild(printFrame);
      
      const printDocument = printFrame.contentWindow.document;
      printDocument.open();
      printDocument.write(htmlContent);
      printDocument.close();
      
      // Set the document title for the PDF filename
      printDocument.title = pdfFileName;
      
      // Wait for content to load, then trigger print
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        
        // Clean up after a delay
        setTimeout(() => {
          document.body.removeChild(printFrame);
        }, 1000);
      }, 500);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleAddCustomer = async (newCustomer) => {
    try {
      const token = getToken();
      
      // Clean the customer data before sending - convert empty email to null
      const customerPayload = {
        ...newCustomer,
        email: newCustomer.email?.trim() || null,  // Convert empty string to null
        notes: newCustomer.notes || '',
      };
      
      const { data } = await axios.post(`${API_BASE_URL}/api/customers/returnId`, customerPayload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
          
      // The API response might not have the name field, so use the original newCustomer.name
      const addedCustomer = {
        ...data,
        name: data.name || newCustomer.name, // Fallback to original name if API doesn't return it
        id: data.id || data._id || Date.now() // Handle different possible ID fields
      };
      
      // Use functional update to ensure we have the latest state
      setCustomers(prevCustomers => {
        const updatedCustomers = [...prevCustomers, addedCustomer];
        console.log('📝 Updated customers array:', updatedCustomers.map(c => c.name));
        return updatedCustomers;
      });

      // Set the customer as selected - use a timeout to ensure state is updated
      setTimeout(() => {
        setCustomerName(addedCustomer.name);
        setSearchQuery(addedCustomer.name);
        setSelectedCustomer(addedCustomer.name);
        setFilteredCustomers([]);
        setAddingCustomer(false);
        
        console.log('✅ Customer selected:', addedCustomer.name);
      }, 100);
      
      // Reset the newCustomer form including notes
      setNewCustomer({
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: '',
        notes: '', // Reset notes field
        showNotes: false, // Reset show notes flag
      });
      
      showCustomModal(
        'Customer Added Successfully! ✅',
        `${addedCustomer.name} has been added to your customer list and is now selected.`,
        [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
      );
    } catch (error) {
      console.error('Error adding customer:', error);
      console.error('Customer creation error response:', error.response?.data);
      
      let errorMessage = 'Failed to add customer. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.response?.data?.message) {
        errorMessage = `Error: ${error.response.data.message}`;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid customer data. Please check all fields.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You do not have access to create customers.';
      } else if (error.response?.status === 422) {
        errorMessage = 'Invalid customer data format. Please check all required fields are filled.';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      showCustomModal(
        'Error',
        errorMessage,
        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
      );
    }
  };

  const canSeePrices = () => {
    return userPermissions?.price_perm === 'visible';
  };

  const hasInvoiceWritePermissions = () => {
    return userPermissions?.invoice_perm === 'write';
  };

  // Check if user has customer write permissions for adding customers inline
  const hasCustomerWritePermissions = () => {
    return userPermissions?.customer_perm === 'write';
  };

  const loadInvoices = async (nextPage = 1) => {
    if (loading || !hasMore) return;
    try {
      setLoading(true);
      const token = getToken();
      const { data } = await axios.get(
        `${API_BASE_URL}/api/invoices?page=${nextPage}&page_size=${PAGE_SIZE}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      const list = Array.isArray(data) ? data : data?.invoices ?? [];

      setInvoices((prev) => (nextPage === 1 ? list : [...prev, ...list]));
      setHasMore(list.length >= PAGE_SIZE);
      setPage(nextPage);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      alert('Failed to load invoices.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setInvoices([]);
    setHasMore(true);
    setPage(1);
    loadInvoices(1);
  }, []);

  const formatDate = (selectedDate, selectedTime) => {
    if (!selectedDate || !selectedTime) return 'Invalid Date';
    const d = new Date(`${selectedDate}T${selectedTime}Z`);
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  // Helper function to format created date
  const formatCreatedDate = (created) => {
    if (!created) return 'Invalid Date';
    const d = new Date(created);
    return d.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    } 
);
  };

  // Helper function to format edited date (only if not default)
  const formatEditedDate = (edited) => {
    if (!edited) return null;
    
    const editedDate = new Date(edited);
    
    // Check if edited date is the default (1970-01-01)
    // Compare year, month, and day to avoid timezone issues
    if (editedDate.getFullYear() === 1970 && 
        editedDate.getMonth() === 0 && 
        editedDate.getDate() === 1) {
      return null;
    }
    
    // Convert to local time and format as date only
    return editedDate.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };

  const renderItem = ({ item }) => {
    if (!item || typeof item !== 'object') return null;

    const createdDate = formatCreatedDate(item.created);
    const editedDate = formatEditedDate(item.edited);
    const idText = `#${item.id ?? 'N/A'}`;

    return (
      <TouchableOpacity
        style={styles.card}
        onPress={() => navigate(`/invoice-details?id=${encodeURIComponent(item.id)}`)}
      >
        <View style={styles.cardHeader}>
          <Text style={styles.customer}>{item.customer || 'Unknown Customer'}</Text>
          <View style={styles.dateContainer}>
            <Text style={styles.dateText}>{idText}</Text>
            <IoCalendarOutline size={16} color="green" style={{ marginHorizontal: 8 }} aria-hidden />
            <Text style={styles.dateText}>{createdDate}</Text>
          </View>
        </View>
        <Text style={styles.title}><Text style={{ fontWeight: 'bold' }}>{item.title}</Text></Text>
        <Text style={styles.description}>Description: {item.description || 'No description'}</Text>
        {canSeePrices() && <Text style={styles.description}>{`Price: $${item.price}`}</Text>}
        {canSeePrices() && (
          <View style={styles.paymentStatusContainer}>
            <Text style={styles.paymentStatusLabel}>Paid in Full: </Text>
            {item.paid === 'yes' ? (
              <Text style={styles.paidYes}>✓</Text>
            ) : (
              <Text style={styles.paidNo}>✗</Text>
            )}
          </View>
        )}
        <Text style={styles.description}>
          {`Work Completed: ${formatDate(item.selectedDate, item.selectedTime)}`}
        </Text>

        <Text style={styles.description}>
          Sent:{' '}
          {item.sentEmail === 'yes' && item.sentText === 'yes'
            ? 'Email, Text'
            : item.sentEmail === 'yes'
            ? 'Email'
            : item.sentText === 'yes'
            ? 'Text'
            : 'No'}
        </Text>

        {editedDate && (
          <Text style={styles.invoiceDetail}>Edited: {editedDate}</Text>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <View style={[styles.mainContainer, isMobile && { flexDirection: 'column' }]}>
      {/* Left side - Invoice list */}
      <View style={[styles.leftPanel, isMobile && { flex: 1, width: '100%', borderRightWidth: 0 }]}>
        <Text style={styles.header}>All Invoices</Text>

        {invoices.length === 0 && !loading ? (
          <Text style={styles.noInvoicesText}>There are no invoices to display.</Text>
        ) : (
          <FlatList
            data={invoices}
            keyExtractor={(item, index) => String(item?.id ?? index)}
            renderItem={renderItem}
            ListFooterComponent={
              hasMore ? (
                <TouchableOpacity
                  style={styles.loadMoreButton}
                  onPress={() => loadInvoices(page + 1)}
                  disabled={loading}
                >
                  <Text style={styles.loadMoreText}>{loading ? 'Loading…' : 'Load More'}</Text>
                </TouchableOpacity>
              ) : null
            }
          />
        )}
      </View>

      {/* Right side - Create invoice form (Hidden on Mobile) */}
      {!isMobile && (
        hasInvoiceWritePermissions() ? (
          <View style={styles.rightPanel}>
            <ScrollView 
              contentContainerStyle={styles.formContainer} 
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={true}
            >
              <Text style={styles.formHeader}>Create New Invoice</Text>
              
              {/* Customer selection */}
              <Text style={styles.label}>Customer Name</Text>
              <TextInput
                style={styles.input}
                value={searchQuery}
                onChangeText={handleSearch}
                onBlur={() => {
                  if (customers.some((c) => c.name === searchQuery)) setFilteredCustomers([]);
                }}
                placeholder="Search or add customer"
                autoComplete="off"
                autoCorrect={false}
              />

              {searchQuery ? (
                <View style={styles.searchResults}>
                  {filteredCustomers.length > 0 ? (
                    filteredCustomers.map((customer, i) => (
                      <TouchableOpacity
                        key={customer.id || `s-${i}`}
                        onPress={() => {
                          setCustomerName(customer.name);
                          setSearchQuery(customer.name);
                          setSelectedCustomer(customer.name);
                          setFilteredCustomers([]);
                        }}
                      >
                        <Text style={styles.searchResult}>{customer.name}</Text>
                      </TouchableOpacity>
                    ))
                  ) : !customers.some((c) => c.name === searchQuery) ? (
                    hasCustomerWritePermissions() ? (
                      <View style={styles.addCustomerRow}>
                        <Text style={styles.searchResult}>{searchQuery}</Text>
                        <TouchableOpacity
                          style={styles.addCustomerButton}
                          onPress={() => {
                            setNewCustomer((nc) => ({ ...nc, name: searchQuery }));
                            setAddingCustomer(true);
                          }}
                        >
                          <MdAdd size={20} color="green" />
                          <Text style={styles.addCustomerText}>Add Customer</Text>
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <Text style={styles.searchResult}>No customer found with that name. You don't have permission to add customers.</Text>
                    )
                  ) : null}
                </View>
              ) : null}

              {/* Inline Add Customer */}
              {addingCustomer && (
                <View style={styles.inlineModal}>
                  <Text style={styles.modalHeader}>Add Customer</Text>
                  {[
                    ['Customer Name', 'name'],
                    ['Address', 'address'],
                    ['City', 'city'],
                    ['State', 'state'],
                    ['Zip', 'zip'],
                    ['Phone', 'phone'],
                    ['Email', 'email'],
                  ].map(([label, key]) => (
                    <View key={key} style={{ marginBottom: 10 }}>
                      <Text style={styles.modalLabel}>{label}</Text>
                      <TextInput
                        style={styles.input}
                        value={newCustomer[key]}
                        onChangeText={(t) => {
                          // Only allow numbers, spaces, dashes, parentheses for phone field
                          if (key === 'phone') {
                            const filtered = t.replace(/[^0-9\s\-()]/g, '');
                            setNewCustomer((nc) => ({ ...nc, [key]: filtered }));
                          } else {
                            setNewCustomer((nc) => ({ ...nc, [key]: t }));
                          }
                        }}
                        placeholder={label}
                        autoComplete="off"
                        autoCorrect={false}
                      />
                    </View>
                  ))}

                  {!newCustomer.showNotes ? (
                    <TouchableOpacity
                      style={styles.addNotesButton}
                      onPress={() => setNewCustomer((nc) => ({ ...nc, showNotes: true }))}
                    >
                      <MdAdd size={20} color="green" />
                      <Text style={styles.addNotesText}>Add Notes</Text>
                    </TouchableOpacity>
                  ) : (
                    <>
                      <TouchableOpacity
                        style={styles.hideNotesButton}
                        onPress={() => setNewCustomer((nc) => ({ ...nc, showNotes: false, notes: '' }))}
                      >
                        <MdRemove size={20} color="red" />
                        <Text style={styles.hideNotesText}>Hide Notes</Text>
                      </TouchableOpacity>
                      <View style={{ marginBottom: 10 }}>
                        <Text style={styles.modalLabel}>Notes (for internal use only):</Text>
                        <TextInput
                          style={[styles.input, styles.textArea]}
                          value={newCustomer.notes}
                          onChangeText={(t) => setNewCustomer((nc) => ({ ...nc, notes: t }))}
                          placeholder="Enter notes here..."
                          multiline
                          numberOfLines={4}
                          autoComplete="off"
                          autoCorrect={false}
                        />
                      </View>
                    </>
                  )}

                  <TouchableOpacity style={styles.saveButton} onPress={() => handleAddCustomer(newCustomer)}>
                    <Text style={styles.saveButtonText}>Save Customer</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.returnButton} onPress={() => setAddingCustomer(false)}>
                    <Text style={styles.returnButtonText}>Return To Invoice</Text>
                  </TouchableOpacity>
                </View>
              )}

              {/* Core invoice fields */}
              <Text style={styles.label}>Title</Text>
              <TextInput 
                style={styles.input} 
                value={title} 
                onChangeText={setTitle}
                autoComplete="off"
                autoCorrect={false}
              />

              <Text style={styles.label}>Description</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={description}
                onChangeText={setDescription}
                multiline
                autoComplete="off"
                autoCorrect={false}
              />

              <Text style={styles.label}>Price</Text>
              <TextInput
                style={styles.input}
                value={price}
                onChangeText={(text) => {
                  // Allow user to type currency formats but clean it for storage
                  const cleanedPrice = text.replace(/[^0-9.]/g, ''); // Remove everything except numbers and decimal points
                  setPrice(cleanedPrice);
                }}
                inputMode="decimal"
                keyboardType="numeric"
                placeholder="0.00"
                autoComplete="off"
                autoCorrect={false}
              />

              <Text style={styles.label}>Work Completed</Text>
              <View style={styles.dateTimeContainer}>
                <Text style={styles.dateTimePreview}>
                  {workCompletedDate ? workCompletedDate.toLocaleString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  }) : 'Select date and time'}
                </Text>
                <input
                  type="datetime-local"
                  value={dateTimeStr}
                  onChange={(e) => {
                    const next = new Date(e.target.value);
                    if (!isNaN(next)) {
                      next.setSeconds(0, 0);
                      setWorkCompletedDate(next);
                    }
                  }}
                  style={htmlDateTimeInputStyle}
                />
              </View>

              <Text style={styles.label}>Payment Status</Text>
              <View style={styles.paymentContainer}>
                <TouchableOpacity
                  style={styles.paymentOption}
                  onPress={() => setPaymentStatus('no')}
                >
                  <View style={[styles.checkbox, paymentStatus === 'no' && styles.checkboxSelected]}>
                    {paymentStatus === 'no' && <Text style={styles.checkmark}>✓</Text>}
                  </View>
                  <Text style={styles.paymentOptionText}>Not Paid</Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={styles.paymentOption}
                  onPress={() => setPaymentStatus('yes')}
                >
                  <View style={[styles.checkbox, paymentStatus === 'yes' && styles.checkboxSelected]}>
                    {paymentStatus === 'yes' && <Text style={styles.checkmark}>✓</Text>}
                  </View>
                  <Text style={styles.paymentOptionText}>Paid in Full</Text>
                </TouchableOpacity>
              </View>

              {!isNotesVisible ? (
                <TouchableOpacity style={styles.addNotesButton} onPress={() => setIsNotesVisible(true)}>
                  <MdAdd size={20} color="green" />
                  <Text style={styles.addNotesText}>Add Notes</Text>
                </TouchableOpacity>
              ) : (
                <>
                  <TouchableOpacity style={styles.hideNotesButton} onPress={() => setIsNotesVisible(false)}>
                    <MdRemove size={20} color="red" />
                    <Text style={styles.hideNotesText}>Hide Notes</Text>
                  </TouchableOpacity>
                  <View style={styles.fieldContainer}>
                    <Text style={styles.label}>Notes (for internal use only):</Text>
                    <TextInput
                      style={[styles.input, styles.textArea]}
                      placeholder="Enter notes here..."
                      multiline
                      numberOfLines={4}
                      onChangeText={setNotes}
                      value={notes}
                      autoComplete="off"
                      autoCorrect={false}
                    />
                  </View>
                </>
              )}

              <TouchableOpacity style={styles.saveButton} onPress={saveInvoice}>
                <Text style={styles.saveButtonText}>Save Invoice</Text>
              </TouchableOpacity>

              <TouchableOpacity style={styles.clearButton} onPress={() => {
                // Clear all form fields
                setTitle('');
                setDescription('');
                setPrice('');
                setNotes('');
                setSearchQuery('');
                setSelectedCustomer('');
                setCustomerName('');
                setFilteredCustomers([]);
                setIsNotesVisible(false);
                setAddingCustomer(false);
                setWorkCompletedDate(() => {
                  const d = new Date();
                  d.setHours(12, 0, 0, 0);
                  return d;
                });
                setNewCustomer({
                  name: '',
                  address: '',
                  city: '',
                  state: '',
                  zip: '',
                  phone: '',
                  email: '',
                  notes: '', // Include notes in reset
                });
              }}>
                <Text style={styles.clearButtonText}>Clear Form</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        ) : (
          <View style={styles.rightPanel}>
            <Text style={styles.readOnlyText}>You have read-only access to invoices.</Text>
          </View>
        )
      )}

      {/* Preview overlay */}
      {previewOpen && (
        <View style={styles.previewOverlay}>
          <ScrollView style={styles.previewCard}>
            <View style={styles.previewHeader}>
              <View style={styles.previewCompanyInfo}>
                <View style={styles.logoContainer}>
                  {businessLogoUrl ? (
                    <img src={businessLogoUrl} alt="Business Logo" style={styles.businessLogo} />
                  ) : (
                    <Text style={styles.logoPlaceholder}>
                      Your business logo will appear here once uploaded by Owner/Admin in Settings.
                    </Text>
                  )}
                </View>
                <Text style={styles.companyName}>{businessInfo?.businessName || 'Your Business Name'}</Text>
                <Text style={styles.companyDetails}>
                  {(() => {
                    if (!businessInfo) {
                      return `123 Business Street\nCity, State 12345\nPhone: (555) 123-4567\nEmail: info@yourbusiness.com`;
                    }
                    
                    const addressParts = [];
                    if (businessInfo.firstName || businessInfo.lastName) {
                      const fullName = [businessInfo.firstName, businessInfo.lastName].filter(Boolean).join(' ');
                      addressParts.push(fullName);
                    }
                    if (businessInfo.address) addressParts.push(businessInfo.address);
                    if (businessInfo.city && businessInfo.state) {
                      const cityState = businessInfo.zip ? `${businessInfo.city}, ${businessInfo.state} ${businessInfo.zip}` : `${businessInfo.city}, ${businessInfo.state}`;
                      addressParts.push(cityState);
                    }
                    if (businessInfo.phone) {
                      addressParts.push(`Phone: ${businessInfo.phone}`);
                    }
                    if (businessInfo.email) {
                      addressParts.push(`Email: ${businessInfo.email}`);
                    }
                    
                    return addressParts.join('\n') || 'Contact information not available';
                  })()}
                </Text>
              </View>
              <View style={styles.previewInvoiceInfo}>
                <Text style={styles.invoiceTitle}>INVOICE</Text>
                <Text style={styles.invoiceDetails}>
                  <Text style={styles.bold}>Invoice #:</Text> INV-{lastCreatedInvoiceId || 'XXXX'}{"\n"}
                  <Text style={styles.bold}>Date:</Text> {new Date().toLocaleDateString()}{"\n"}
                  <Text style={styles.bold}>Due Date:</Text> {new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}
                </Text>
              </View>
            </View>

            <View style={styles.customerSection}>
              <Text style={styles.sectionTitle}>Invoice To:</Text>
              <View style={styles.customerInfo}>
                <Text style={styles.customerName}>{previewInvoiceData.customer || 'Customer Name'}</Text>
                {(() => {
                  const customer = customers.find(c => c.name === previewInvoiceData.customer);
                  if (customer) {
                    return (
                      <>
                        {customer.address && <Text>{customer.address}</Text>}
                        {customer.city && customer.state && (
                          <Text>{customer.city}, {customer.state} {customer.zip || ''}</Text>
                        )}
                        {customer.phone && <Text>Phone: {customer.phone}</Text>}
                        {customer.email && <Text>Email: {customer.email}</Text>}
                      </>
                    );
                  }
                  return null;
                })()}
              </View>
            </View>

            <View style={styles.servicesSection}>
              <Text style={styles.sectionTitle}>Services Provided</Text>
              <View style={styles.serviceItem}>
                <Text style={styles.serviceTitle}>{previewInvoiceData.title || 'Service Title'}</Text>
                <Text style={styles.serviceDescription}>{previewInvoiceData.description || 'No description provided.'}</Text>
                <Text style={styles.serviceDate}>
                  Work completed on: {(() => {
                    if (previewInvoiceData.selectedDate && previewInvoiceData.selectedTime) {
                      // Parse as UTC and convert to local time
                      const utcDate = new Date(previewInvoiceData.selectedDate + 'T' + previewInvoiceData.selectedTime + 'Z');
                      const formattedDate = utcDate.toLocaleDateString();
                      const formattedTime = utcDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                      return `${formattedDate} at ${formattedTime}`;
                    }
                    return 'Date not specified';
                  })()}
                </Text>
              </View>
            </View>

            <View style={styles.pricingSection}>
  <View style={styles.priceRow}>
    <Text style={styles.priceLabel}>Service Total:</Text>
    <Text style={styles.priceAmount}>${parseFloat(previewInvoiceData.price || 0).toFixed(2)}</Text>
  </View>
  {previewInvoiceData.paid === 'yes' && (
    <View style={[styles.priceRow, { borderBottomWidth: 0 }]}>
      <Text style={[styles.priceLabel, { color: '#22c55e', fontWeight: 'bold' }]}>Paid in Full</Text>
      <Text style={{ color: '#22c55e', fontSize: 18, fontWeight: 'bold' }}>✓</Text>
    </View>
  )}
  <View style={[styles.priceRow, styles.totalRow]}>
    <Text style={styles.totalLabel}>Amount Due:</Text>
    <Text style={styles.totalAmount}>
      ${previewInvoiceData.paid === 'yes' ? '0.00' : parseFloat(previewInvoiceData.price || 0).toFixed(2)}
    </Text>
  </View>
</View>

            <View style={styles.paymentSection}>
              <Text style={styles.paymentTitle}>Payment Information</Text>
              <Text style={styles.paymentText}>
                • Payment is due within 30 days of invoice date{"\n"}
                • Please include invoice number with payment{"\n"}
                • Late payments may be subject to additional fees{"\n"}
                • Contact us with any questions about this invoice
              </Text>
            </View>

            <View style={styles.footer}>
              <Text>Thank you for your business!</Text>
              <Text>
                <Text style={styles.bold}>Questions?</Text> Contact us at {businessInfo?.phone || '(555) 123-4567'} or {businessInfo?.email || 'info@yourbusiness.com'}
              </Text>
            </View>

            <View style={styles.modalButtons}>
  <TouchableOpacity 
    style={[styles.modalButton, styles.emailModalButton, sendingEmail && { opacity: 0.7 }]}
    onPress={async () => {
      if (!lastCreatedInvoiceId) {
        showCustomModal(
          'Error',
          'Invoice ID not available. Please try emailing from the invoices list.',
          [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
        );
        return;
      }

      const customerData = customers.find(
        (c) => (c?.name ?? '').trim().toLowerCase() === (previewInvoiceData.customer || '').trim().toLowerCase()
      );
      
      if (!customerData?.email) {
        showCustomModal(
          'Email Not Available ⚠️',
          'Customer email not found. Please update customer information.',
          [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
        );
        return;
      }

      setPreviewOpen(false);
      
      try {
        setSendingEmail(true);
        
        showCustomModal(
          'Sending Email...',
          'Please wait while we send your invoice.',
          [
            {
              text: 'Sending…',
              style: 'primary',
              disabled: true,
              onPress: () => {}
            }
          ]
        );

        const token = getToken();
        // Get user's timezone offset in minutes
        const timezoneOffset = new Date().getTimezoneOffset();
          
        const emailResponse = await axios.post(`${API_BASE_URL}/api/invoices/${lastCreatedInvoiceId}/email`, {
          timezoneOffset: timezoneOffset  // Send timezone offset to backend
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (emailResponse.data && emailResponse.data.success) {
          showCustomModal(
            'Email Sent Successfully! ✅',
            `Invoice has been emailed as PDF to ${customerData.email}`,
            [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
          );
        } else {
          showCustomModal(
            'Email Status Unclear ⚠️',
            emailResponse.data?.message || 'Email may have been sent, but status is unclear.',
            [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
          );
        }
      } catch (emailError) {
        console.error('Error sending invoice email:', emailError);
        
        let errorMessage = 'Failed to send email. Please try again.';
        if (emailError.response?.status === 400) {
          errorMessage = `Customer email issue. Please update customer information.`;
        } else if (emailError.response?.data?.detail) {
          errorMessage = emailError.response.data.detail;
        }
        
        showCustomModal(
          'Email Failed ❌',
          errorMessage,
          [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
        );
      } finally {
        setSendingEmail(false);
      }
    }}
    disabled={sendingEmail}
  >
    <Text style={styles.previewActionButtonText}>
      {sendingEmail ? 'Sending…' : '📧 One-Touch Email'}
    </Text>
  </TouchableOpacity>

  <TouchableOpacity 
    style={[styles.modalButton, styles.savePdfButton]} 
    onPress={() => generateAndSharePDF(previewInvoiceData, lastCreatedInvoiceId, businessInfo, previewCustomerData)}
  >
    <Text style={styles.previewActionButtonText}>💾 Save/Print PDF</Text>
  </TouchableOpacity>
</View>

<TouchableOpacity 
  style={styles.closePreviewLink}
  onPress={() => setPreviewOpen(false)}
>
  <Text style={styles.closePreviewButtonText}>Close Preview</Text>
</TouchableOpacity>
          </ScrollView>
        </View>
      )}

      {/* Custom Modal */}
      {customModal.open && (
        <View style={styles.previewOverlay}>
          <View style={styles.previewCard}>
            <Text style={styles.previewTitle}>{customModal.title}</Text>
            <Text>{customModal.message}</Text>
            <View style={styles.buttonGroup}>
              {customModal.buttons.map((button, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.actionButton,
                    button.style === 'primary' && { backgroundColor: 'blue' },
                    button.style === 'secondary' && { backgroundColor: 'gray' },
                    button.style === 'danger' && { backgroundColor: 'red' },
                    button.style === 'default' && { backgroundColor: '#f0f0f0' },
                  ]}
                  onPress={button.onPress}
                >
                  <Text style={[
                    styles.actionButtonText,
                    button.style === 'default' && { color: 'black' }
                  ]}>
                    {button.text}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  mainContainer: { 
    flex: 1, 
    flexDirection: 'row', 
    height: '100vh', 
    backgroundColor: '#fff',
    paddingRight: 656, // Space for fixed right panel (620px + 36px gap)
  },
  leftPanel: { 
    width: '100%',
    maxWidth: 800, // Constrain the left panel width
    padding: 20, 
    paddingBottom: 50, 
    backgroundColor: '#fff',
  },
  rightPanel: { 
    position: 'fixed',
    right: 16,
    top: 90,
    width: 620,
    maxHeight: '90vh',
    overflow: 'auto',
    backgroundColor: '#ffffff',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    zIndex: 2,
    boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
  },
  formContainer: { 
    paddingBottom: 50 
  },
  formHeader: {
    fontSize: 20, 
    fontWeight: 'bold', 
    marginBottom: 20, 
    textAlign: 'center',
    color: '#333'
  },
  header: {
    fontSize: 24, fontWeight: 'bold', marginBottom: 20, textAlign: 'center',
    textDecorationLine: 'underline',
  },

  // Invoice list styles
  card: {
    backgroundColor: '#f0f0f0',
    padding: 15,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 8,
    marginBottom: 15,
  },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  customer: { fontSize: 16, fontWeight: 'bold', marginBottom: 5 },
  title: { fontSize: 16, fontWeight: 'bold', marginBottom: 5 },
  description: { fontSize: 14, color: '#555', marginBottom: 5 },
  price: { fontSize: 14, color: '#000', fontWeight: 'bold' },
  invoiceDetail: { fontSize: 12, color: '#555', marginTop: 5 },

  dateContainer: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  dateText: { fontSize: 14, color: 'green', fontWeight: 'bold', marginHorizontal: 0 },

  noInvoicesText: { textAlign: 'center', fontSize: 16, color: '#777', marginTop: 50 },

  loadMoreButton: {
    padding: 10, backgroundColor: '#007BFF', borderRadius: 5, alignItems: 'center', marginVertical: 10,
  },
  loadMoreText: { color: '#fff', fontSize: 16 },

  // Create invoice form styles
  label: { marginTop: 15, fontWeight: 'bold' },
  modalLabel: { fontWeight: 'bold', marginBottom: 5 },
  input: { borderWidth: 1, borderColor: '#ccc', padding: 8, borderRadius: 4 },
  textArea: { height: 100, textAlignVertical: 'top' },
  row: { flexDirection: 'row', gap: 10, marginTop: 10 },
  flex1: { flex: 1 },

  // Date/Time picker styles
  dateTimeContainer: { marginTop: 10 },
  dateTimePreview: { 
    fontSize: 14, 
    color: '#333', 
    marginBottom: 6,
    fontWeight: 'bold'
  },

  searchResults: { marginTop: 5, backgroundColor: '#f9f9f9', borderRadius: 4, padding: 10 },
  searchResult: { paddingVertical: 5, fontSize: 14 },
  addCustomerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  addCustomerButton: { flexDirection: 'row', alignItems: 'center', marginLeft: 10 },
  addCustomerText: { color: 'green', marginLeft: 5 },

  saveButton: { marginTop: 20, backgroundColor: 'blue', padding: 12, borderRadius: 6, alignItems: 'center' },
  saveButtonText: { color: 'white', fontWeight: 'bold' },
  clearButton: { marginTop: 10, backgroundColor: 'red', padding: 12, borderRadius: 6, alignItems: 'center' },
  clearButtonText: { color: 'white', fontWeight: 'bold' },
  returnButton: { marginTop: 10, backgroundColor: '#666', padding: 12, borderRadius: 6, alignItems: 'center' },
  returnButtonText: { color: 'white', fontWeight: 'bold' },

  fieldContainer: { marginTop: 0 },
  addNotesButton: { flexDirection: 'row', alignItems: 'center', marginTop: 10 },
  addNotesText: { color: 'green', marginLeft: 5 },
  hideNotesButton: { flexDirection: 'row', alignItems: 'center', marginTop: 10 },
  hideNotesText: { color: 'red', marginLeft: 5 },

  inlineModal: {
    marginTop: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    backgroundColor: '#fff',
  },
  modalHeader: { fontSize: 18, fontWeight: 'bold', marginBottom: 15 },

  readOnlyText: { textAlign: 'center', fontSize: 16, color: '#777', marginTop: 50 },

  // preview overlay
  previewOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    zIndex: 1000,
  },
  previewCard: {
    width: 'min(640px, 100%)',
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    paddingBottom: 60,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingTop: 60,
  },
  previewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    paddingBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: '#dc2626',
  },
  previewCompanyInfo: {
    flex: 1,
    paddingRight: 20,
  },
  logoContainer: {
    marginBottom: 10,
    minHeight: 85,
  },
  businessLogo: {
    maxWidth: 150,
    maxHeight: 80,
    objectFit: 'contain',
  },
  logoPlaceholder: {
    fontSize: 12,
    color: '#666',
  },
  companyName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#dc2626',
    marginBottom: 10,
  },
  companyDetails: {
    fontSize: 13,
    color: '#666',
  },
  previewInvoiceInfo: {
    flex: 1,
    alignItems: 'flex-end',
  },
  invoiceTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#dc2626',
  },
  invoiceDetails: {
    fontSize: 13,
    color: '#666',
    marginTop: 8,
  },
  customerSection: {
    marginBottom: 30,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
    paddingBottom: 5,
    borderBottomWidth: 2,
    borderBottomColor: '#e5e7eb',
  },
  customerInfo: {
    backgroundColor: '#fef2f2',
    padding: 20,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#dc2626',
  },
  customerName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 10,
  },
  servicesSection: {
    marginBottom: 30,
  },
  serviceItem: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    padding: 25,
    marginBottom: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  serviceTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 15,
  },
  serviceDescription: {
    color: '#4b5563',
    marginBottom: 15,
    lineHeight: 1.7,
    whiteSpace: 'pre-wrap',
  },
  serviceDate: {
    color: '#6b7280',
    fontSize: 14,
    fontStyle: 'italic',
  },
  pricingSection: {
    backgroundColor: '#fef2f2',
    borderRadius: 8,
    padding: 25,
    marginBottom: 30,
    borderWidth: 2,
    borderColor: '#dc2626',
  },
  priceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#fecaca',
  },
  totalRow: {
    borderBottomWidth: 0,
    fontSize: 24,
    fontWeight: 'bold',
    color: '#dc2626',
    marginTop: 10,
    paddingTop: 20,
    borderTopWidth: 2,
    borderTopColor: '#dc2626',
  },
  priceLabel: {
    fontSize: 18,
    color: '#374151',
  },
  priceAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  totalLabel: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  totalAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#dc2626',
  },
  paymentSection: {
    marginTop: 40,
    padding: 20,
    backgroundColor: '#f0fdf4',
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#22c55e',
  },
  paymentTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#166534',
    marginBottom: 10,
  },
  paymentText: {
    fontSize: 14,
    color: '#15803d',
  },
  footer: {
    marginTop: 50,
    paddingTop: 20,
    borderTopWidth: 2,
    borderTopColor: '#e5e7eb',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: 14,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 20,
  },
  modalButton: {
    flex: 1,
    padding: 12,
    borderRadius: 6,
    alignItems: 'center',
  },
  emailModalButton: {
    backgroundColor: 'green',
  },
  savePdfButton: {
    backgroundColor: 'blue',
  },
  previewActionButtonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 14,
  },
  closePreviewLink: {
    alignItems: 'center',
    marginTop: 12,
    padding: 8,
  },
  closePreviewButtonText: {
    color: '#FF3B30',
    fontSize: 16,
    textDecorationLine: 'underline',
  },
  buttonGroup: {
    marginTop: 16,
  },
  actionButton: {
    backgroundColor: '#f0f0f0',
    padding: 12,
    borderRadius: 6,
    alignItems: 'center',
    marginBottom: 10,
  },
  actionButtonText: {
    color: 'white',
    fontWeight: 'bold',
  },
  pdfButton: {
    backgroundColor: '#d3d3d3',
  },
  emailButton: {
    backgroundColor: '#d3d3d3',
  },
  pdfButtonText: {
    color: 'black',
    fontWeight: 'bold',
  },
  emailButtonText: {
    color: 'black',
    fontWeight: 'bold',
  },
  paymentContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 30,
    marginTop: 10,
  },
  paymentOption: { flexDirection: 'row', alignItems: 'center' },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    marginRight: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxSelected: { backgroundColor: 'blue' },
  checkmark: { color: 'white', fontWeight: 'bold' },
  paymentOptionText: { fontSize: 14, color: '#333' },
  paymentStatusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 5,
  },
  paymentStatusLabel: {
    fontSize: 14,
    color: '#555',
  },
  paidYes: {
    fontSize: 14,
    color: 'green',
    fontWeight: 'bold',
  },
  paidNo: {
    fontSize: 14,
    color: 'red',
    fontWeight: 'bold',
  },
});
const htmlDateTimeInputStyle = {
  padding: '8px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  marginRight: '10px',
  flex: 1,
};